import React, { useState } from 'react';
import {
  downloadTrainingDocument,
  downloadDbgDocument,
} from 'services/BenGuideService';
import { useAppSelector } from 'hooks/redux';
import { SourceItem } from './CollapsibleSources';

import styles from './expandedSources.module.less';

// Source type categorization types
type SourceTypeKey = 'BENEFIT_GUIDE' | 'EMPLOYER' | 'WEB';
type SourceTypeLabels = Record<SourceTypeKey, string>;

interface ExpandedSourcesProps {
  /** Array of source items with title, URL, and optional snippet */
  sources: SourceItem[];
  /** Legacy single reference URL */
  reference?: string;
  /** Label for single source link */
  singleSourceLabel?: string;
  /** Function to categorize sources by type */
  categorizeSources?: (
    sources: SourceItem[]
  ) => Map<SourceTypeKey, SourceItem[]>;
  /** Labels for source types */
  sourceTypeLabels?: SourceTypeLabels;
  /** Optional callback when a plan name is clicked */
  onPlanClick?: (response: any) => void;
  /** Optional callback when a document download is initiated */
  onDocumentDownload?: (documentId: string, employerId: string) => void;
  /** Employer ID from chat context for document downloads */
  employerId?: string;
}

const ExpandedSources: React.FC<ExpandedSourcesProps> = ({
  sources,
  reference,
  singleSourceLabel = 'View Source',
  categorizeSources,
  sourceTypeLabels,
  onPlanClick,
  onDocumentDownload,
  employerId,
}) => {
  // Get benefit guide data from Redux store
  const benguideData = useAppSelector((state) => state.benguide.data);

  // Section to benefit kind mapping
  const sectionToBenefitKind = {
    medical: 'health-benefit/medical',
    dental: 'dental',
    vision: 'vision',
    lifeAndDisability: 'life-and-disability',
    fourOOneK: '401k',
    hsa: 'tax-advantaged/hsa',
    hra: 'tax-advantaged/hra',
    fsa: 'tax-advantaged/fsa',
    commuter: 'tax-advantaged/commuter',
    volBenefits: 'worksite',
    telehealth: 'telehealth',
    wellbeing: 'wellbeing',
    additionalPerks: 'additional-perks',
    familyAndLeave: 'family-and-leave',
    holidaysAndTimeOff: 'holidays-time-off',
  };

  // Reusable function to find plan details from Redux store
  const findPlanDetails = (masterId: string) => {
    if (!benguideData || !masterId) return null;

    const findPlanInSection = (sectionPlans: any[]) => {
      return sectionPlans?.find((plan: any) => plan.planId === masterId);
    };

    const sections = [
      { name: 'medical', plans: benguideData.medical?.plans || [] },
      { name: 'dental', plans: benguideData.dental?.plans || [] },
      { name: 'vision', plans: benguideData.vision?.plans || [] },
      {
        name: 'lifeAndDisability',
        plans: benguideData.lifeAndDisability?.plans || [],
      },
      { name: 'fourOOneK', plans: benguideData.fourOOneK?.plans || [] },
      { name: 'hsa', plans: benguideData.hsa?.plans || [] },
      { name: 'hra', plans: benguideData.hra?.plans || [] },
      { name: 'fsa', plans: benguideData.fsa?.plans || [] },
      { name: 'commuter', plans: benguideData.commuter?.plans || [] },
      { name: 'volBenefits', plans: benguideData.volBenefits?.plans || [] },
      { name: 'wellbeing', plans: benguideData.wellbeing?.plans || [] },
      { name: 'telehealth', plans: benguideData.telehealth?.plans || [] },
      {
        name: 'additionalPerks',
        plans: benguideData.additionalPerks?.plans || [],
      },
      {
        name: 'familyAndLeave',
        plans: benguideData.familyAndLeave?.plans || [],
      },
      {
        name: 'holidaysAndTimeOff',
        plans: benguideData.holidaysAndTimeOff?.plans || [],
      },
    ];

    for (const section of sections) {
      const foundPlan = findPlanInSection(section.plans);
      if (foundPlan) {
        const benefitKind =
          sectionToBenefitKind[
            section.name as keyof typeof sectionToBenefitKind
          ];
        return {
          plan: foundPlan,
          benefitKind,
          planName: foundPlan.planName || foundPlan.name || foundPlan.title,
        };
      }
    }

    return null;
  };

  // Handle plan name click
  const handlePlanClick = (sourceItem: SourceItem, event: React.MouseEvent) => {
    if (sourceItem.masterId && sourceItem.type === 'BENEFIT_GUIDE') {
      event.preventDefault();

      const planDetails = findPlanDetails(sourceItem.masterId);

      if (planDetails && onPlanClick) {
        onPlanClick(planDetails.benefitKind);
      } else if (!planDetails) {
        console.warn(
          'Plan not found in Redux store for masterId:',
          sourceItem.masterId
        );
      }
    }
  };

  // Track loading state for document downloads
  const [loadingDocumentId, setLoadingDocumentId] = useState<string | null>(
    null
  );

  // Handle document download click
  const handleDocumentDownload = async (
    sourceItem: SourceItem,
    event: React.MouseEvent
  ) => {
    if (!sourceItem.masterId) return;

    event.preventDefault();
    setLoadingDocumentId(sourceItem.masterId);
    try {
      // Extract fileName from title (assuming title contains the file name with extension)
      const fileName = sourceItem.title;

      if (sourceItem.sourceType === 'ai_training' && employerId) {
        await downloadTrainingDocument(
          employerId,
          sourceItem.masterId,
          fileName
        );
        if (onDocumentDownload) {
          onDocumentDownload(sourceItem.masterId, employerId);
        }
      } else if (sourceItem.sourceType === 'dbg_attached') {
        await downloadDbgDocument(sourceItem.masterId, fileName);
        if (onDocumentDownload) {
          onDocumentDownload(sourceItem.masterId, employerId || '');
        }
      }
    } catch (error) {
      console.error('Failed to download document:', error);
    } finally {
      setLoadingDocumentId(null);
    }
  };

  // Render source link with conditional click handler
  const renderSourceLink = (sourceItem: SourceItem) => {
    const isDownloadableDocument = Boolean(
      sourceItem.masterId &&
        ((sourceItem.sourceType === 'ai_training' && employerId) ||
          sourceItem.sourceType === 'dbg_attached')
    );

    const isClickablePlan =
      sourceItem.masterId &&
      sourceItem.type === 'BENEFIT_GUIDE' &&
      !isDownloadableDocument;

    if (isClickablePlan) {
      // Get plan name from Redux store for clickable plans
      const planDetails = findPlanDetails(sourceItem.masterId!);
      const displayTitle = planDetails?.planName || sourceItem.title;

      return (
        <button
          type="button"
          onClick={(e) => handlePlanClick(sourceItem, e)}
          className={`${styles.sourceLink} ${styles.planLink}`}
          aria-label={`Get details for plan: ${displayTitle}`}
        >
          {displayTitle ?? `Open source: ${sourceItem?.url}`}
        </button>
      );
    }

    if (isDownloadableDocument) {
      const isLoading = loadingDocumentId === sourceItem.masterId;
      return (
        <button
          type="button"
          onClick={(e) => handleDocumentDownload(sourceItem, e)}
          className={`${styles.sourceLink} ${styles.planLink}`}
          aria-label={`Download document: ${sourceItem.title}`}
          disabled={isLoading}
        >
          {isLoading && <span className={styles.documentSpinner} />}
          {sourceItem.title ?? 'Download Document'}
        </button>
      );
    }

    return (
      <a
        href={sourceItem?.url}
        target="_blank"
        rel="noopener noreferrer"
        className={styles.sourceLink}
        aria-label={`Open source: ${sourceItem?.title}`}
      >
        {sourceItem.title ?? `Open source: ${sourceItem?.url}`}
      </a>
    );
  };

  // Render categorized sources if categorization function is provided
  const renderCategorizedSources = () => {
    if (!categorizeSources || !sourceTypeLabels) {
      return renderFlatSources();
    }

    const categorizedSources = categorizeSources(sources);
    const hasAnySources = Array.from(categorizedSources.values()).some(
      (categoryItems) => categoryItems.length > 0
    );

    if (!hasAnySources) {
      return null;
    }

    return (
      <div className={styles.categorizedSources}>
        {Array.from(categorizedSources.entries()).map(
          ([type, categoryItems]) => {
            // Only render category if it has sources
            if (categoryItems.length === 0) {
              return null;
            }

            return (
              <div key={type} className={styles.sourceCategory}>
                <h4 className={styles.categoryTitle}>
                  {sourceTypeLabels[type]}
                </h4>
                <ul className={styles.sourcesList} role="list">
                  {categoryItems.map((sourceItem, index) => (
                    <li
                      key={`${type}-${index}`}
                      className={styles.sourcesListItem}
                      role="listitem"
                    >
                      {renderSourceLink(sourceItem)}
                      {sourceItem?.snippet && (
                        <p
                          className={styles.sourceSnippet}
                          aria-label="Source excerpt"
                        >
                          {sourceItem?.snippet}
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            );
          }
        )}
      </div>
    );
  };

  // Render flat list of sources (fallback)
  const renderFlatSources = () => (
    <ul className={styles.sourcesList} role="list">
      {sources.map((sourceItem, index) => (
        <li
          key={`source-${index}`}
          className={styles.sourcesListItem}
          role="listitem"
        >
          {renderSourceLink(sourceItem)}
          {sourceItem.snippet && (
            <p className={styles.sourceSnippet} aria-label="Source excerpt">
              {sourceItem.snippet}
            </p>
          )}
        </li>
      ))}
    </ul>
  );

  return (
    <div
      id="sources-content"
      className={styles.sourcesContent}
      role="region"
      aria-label="Source references"
    >
      {sources.length > 0 ? (
        renderCategorizedSources()
      ) : reference ? (
        <div className={styles.singleSourceContainer}>
          <a
            href={reference}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.sourceLink}
            aria-label="Open source reference"
          >
            {singleSourceLabel}
          </a>
        </div>
      ) : null}
    </div>
  );
};

export default ExpandedSources;
